/**
 * Shared public request contracts for execution-engine callers and the Worker.
 *
 * This module intentionally contains only public wire schemas. Workflow
 * payloads, funding gates, provider registries, and venue/cash capability
 * policy remain app-local. Fields already normalized by the server continue
 * to normalize here; generic CAIP asset ids preserve their original casing
 * because that casing is part of established operation fingerprints.
 */
import {
  Parser,
  Validator,
  canonicalizeAssetIdLike,
  type AssetId,
} from "@repo/caip";
import {
  ExecuteActionInputSchema,
  ExecutionActionFailurePolicySchema,
  ExecutionActionScopeWireSchema,
} from "@repo/onchain-execution-pipeline/schema";
import { z } from "zod";
import { SellBalanceInputSchema } from "./sell-balance.js";
import {
  BASE_WETH_USDC_ASSET_IDS,
  RestrictedRebalanceScopeSchema,
} from "./restricted-rebalance-scope.js";
export {
  BASE_WETH_USDC_ASSET_IDS,
  BASE_WETH_USDC_EXECUTION_SCOPE,
  RestrictedRebalanceScopeSchema,
  RESTRICTED_REBALANCE_SCOPE_HASH_DOMAIN,
  hashRestrictedRebalanceScope,
  type RestrictedRebalanceScope,
} from "./restricted-rebalance-scope.js";

export const MAX_EXECUTION_SIGNAL_BYTES = 64 * 1024;
export const MAX_EXECUTION_SIGNAL_COLLECTION_ITEMS = 64;

export const ExecutionSignalIdSchema = z.string().trim().min(1).max(128);

export type ExecutionSignalJsonValue =
  | null
  | boolean
  | number
  | string
  | readonly ExecutionSignalJsonValue[]
  | { readonly [key: string]: ExecutionSignalJsonValue };

const ExecutionSignalJsonValueSchema: z.ZodType<ExecutionSignalJsonValue> =
  z.lazy(() =>
    z.union([
      z.null(),
      z.boolean(),
      z.number().finite(),
      z.string(),
      z.array(ExecutionSignalJsonValueSchema),
      z.record(z.string(), ExecutionSignalJsonValueSchema),
    ]),
  );

const hasOwn = (record: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(record, key);

const isUnknownRecord = (
  value: unknown,
): value is Readonly<Record<string, unknown>> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const addCollectionBoundIssue = (params: {
  readonly record: Readonly<Record<string, unknown>>;
  readonly key: string;
  readonly pathPrefix: readonly (string | number)[];
  readonly ctx: z.RefinementCtx;
}): void => {
  if (!hasOwn(params.record, params.key)) return;

  const value = params.record[params.key];
  if (!Array.isArray(value)) {
    params.ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `${params.key} must be an array`,
      path: [...params.pathPrefix, params.key],
    });
    return;
  }

  if (value.length > MAX_EXECUTION_SIGNAL_COLLECTION_ITEMS) {
    params.ctx.addIssue({
      code: z.ZodIssueCode.too_big,
      maximum: MAX_EXECUTION_SIGNAL_COLLECTION_ITEMS,
      origin: "array",
      inclusive: true,
      message: `${params.key} must contain at most ${MAX_EXECUTION_SIGNAL_COLLECTION_ITEMS} entries`,
      path: [...params.pathPrefix, params.key],
    });
  }
};

const addHashIssue = (params: {
  readonly value: unknown;
  readonly path: readonly (string | number)[];
  readonly ctx: z.RefinementCtx;
}): void => {
  if (
    typeof params.value !== "string" ||
    !/^0x[a-fA-F0-9]{64}$/u.test(params.value)
  ) {
    params.ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "must be a 32-byte 0x-prefixed hash",
      path: [...params.path],
    });
  }
};

const addHashArrayIssues = (params: {
  readonly record: Readonly<Record<string, unknown>>;
  readonly key: string;
  readonly pathPrefix: readonly (string | number)[];
  readonly ctx: z.RefinementCtx;
}): void => {
  addCollectionBoundIssue(params);
  const value = params.record[params.key];
  if (!Array.isArray(value)) return;

  const validationLength = Math.min(
    value.length,
    MAX_EXECUTION_SIGNAL_COLLECTION_ITEMS,
  );
  for (let index = 0; index < validationLength; index += 1) {
    addHashIssue({
      value: value[index],
      path: [...params.pathPrefix, params.key, index],
      ctx: params.ctx,
    });
  }
};

const addSignalEvidenceIssues = (
  evidence: Readonly<Record<string, unknown>>,
  ctx: z.RefinementCtx,
): void => {
  const visit = (value: unknown, path: readonly (string | number)[]): void => {
    if (Array.isArray(value)) {
      for (let index = 0; index < value.length; index += 1) {
        visit(value[index]!, [...path, index]);
      }
      return;
    }
    if (!isUnknownRecord(value)) return;

    if (hasOwn(value, "txHash")) {
      addHashIssue({ value: value.txHash, path: [...path, "txHash"], ctx });
    }
    for (const key of [
      "txHashes",
      "allowedUserOpHashes",
      "userOpHashes",
    ] as const) {
      if (hasOwn(value, key)) {
        addHashArrayIssues({ record: value, key, pathPrefix: path, ctx });
      }
    }
    for (const key of [
      "settlementTransactions",
      "settlementRequirements",
      "requiredMovements",
    ] as const) {
      addCollectionBoundIssue({ record: value, key, pathPrefix: path, ctx });
    }

    for (const [key, child] of Object.entries(value)) {
      visit(child, [...path, key]);
    }
  };

  visit(evidence, []);
};

const addSerializedSizeIssue = (
  value: ExecutionSignalJsonValue | Readonly<Record<string, unknown>>,
  ctx: z.RefinementCtx,
): void => {
  const serialized = JSON.stringify(value);
  const byteLength = new TextEncoder().encode(serialized).byteLength;
  if (byteLength <= MAX_EXECUTION_SIGNAL_BYTES) return;

  ctx.addIssue({
    code: z.ZodIssueCode.too_big,
    maximum: MAX_EXECUTION_SIGNAL_BYTES,
    origin: "string",
    inclusive: true,
    message: `serialized signal payload must be at most ${MAX_EXECUTION_SIGNAL_BYTES} bytes`,
  });
};

const BoundedExecutionSignalEvidenceInputSchema = z
  .record(z.string(), z.unknown())
  .superRefine(addSignalEvidenceIssues);

export const ExecutionSignalEvidenceSchema =
  BoundedExecutionSignalEvidenceInputSchema.pipe(
    z
      .record(z.string(), ExecutionSignalJsonValueSchema)
      .superRefine(addSerializedSizeIssue),
  );

export const PositiveIntegerAmountSchema = z
  .string()
  .regex(/^(?:[1-9]\d*)$/u)
  .max(80);

export const NonNegativeIntegerAmountSchema = z
  .string()
  .regex(/^(?:0|[1-9]\d*)$/u)
  .max(80);

export const AssetIdSchema = z
  .string()
  .refine((value): boolean => Validator.isValidAssetId(value), {
    message: "Invalid assetId",
  });

// Keep the public value type as `string`: callers should not need to
// manufacture CAIP brands merely to submit already runtime-validated JSON.
const ChainIdSchema = z
  .string()
  .refine((value): boolean => Validator.isValidChainId(value), {
    message: "Invalid chainRef",
  });

/**
 * Lowercasing is part of the public boundary contract: request-key retries
 * that differ only in EVM checksum casing must produce identical inputs.
 */
export const EvmAddressSchema = z
  .string()
  .regex(/^0x[a-fA-F0-9]{40}$/u)
  .transform((value) => value.toLowerCase());

export const EvmTxHashSchema = z.string().regex(/^0x[a-fA-F0-9]{64}$/u);

const AccountIdSchema = z
  .string()
  .refine((value): boolean => Validator.isValidAccountId(value), {
    message: "Invalid accountRef",
  });

export const EvmErc20AssetIdSchema = z
  .string()
  .trim()
  .superRefine((assetId, ctx) => {
    if (!Validator.isValidAssetId(assetId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "asset id must be a valid CAIP-19 id",
      });
      return;
    }

    try {
      const parsed = Parser.parseAssetId(assetId as AssetId);
      if (
        parsed.chain.namespace !== "eip155" ||
        parsed.asset.namespace !== "erc20" ||
        !/^0x[a-fA-F0-9]{40}$/u.test(parsed.asset.reference)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "asset id must identify an eip155 ERC-20 token",
        });
      }
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "asset id must be a parseable CAIP-19 id",
      });
    }
  })
  .transform((assetId) => canonicalizeAssetIdLike(assetId));

export const EvmAccountIdSchema = z
  .string()
  .trim()
  .superRefine((accountId, ctx) => {
    if (!Validator.isValidAccountId(accountId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "destination account must be a valid CAIP-10 account id",
      });
      return;
    }

    try {
      const parsed = Parser.parseAccountId(accountId);
      const chainId = Number(parsed.chain.reference);
      if (
        parsed.chain.namespace !== "eip155" ||
        !/^[1-9]\d*$/u.test(parsed.chain.reference) ||
        !Number.isSafeInteger(chainId) ||
        !/^0x[a-fA-F0-9]{40}$/u.test(parsed.account.address)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "destination account must identify an eip155 EVM account",
        });
      }
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "destination account must be a parseable CAIP-10 account id",
      });
    }
  })
  .transform((accountId) => accountId.toLowerCase());

export const ExecutionRequestKeySchema = z.string().trim().min(1).max(512);
export const ExecutionCorrelationIdSchema = z.string().trim().min(1).max(128);
export const ExecutionPortfolioIdSchema = z.string().trim().min(1).max(256);

export const MarketOrderRfqProviderSchema = z.enum([
  "bebop_rfq",
  "bebop_jam",
  "ondo_direct",
  "reality_via_bitget",
  "lifi",
  "relay",
  "uniswap_v3",
]);

export const LifiMarketOrderVenueKeySchema = z.enum(["lifi", "uniswap_v3"]);

export const ExpectedMarketOrderVenueSchema = z.discriminatedUnion("provider", [
  z.object({ provider: z.literal("bebop_rfq") }).strict(),
  z.object({ provider: z.literal("bebop_jam") }).strict(),
  z.object({ provider: z.literal("ondo_direct") }).strict(),
  z.object({ provider: z.literal("reality_via_bitget") }).strict(),
  z.object({ provider: z.literal("relay") }).strict(),
  z
    .object({
      provider: z.literal("lifi"),
      quoteVenueKey: LifiMarketOrderVenueKeySchema,
    })
    .strict(),
]);

const MarketOrderExecutionProviderSchema = z.enum([
  "bebop_rfq",
  "bebop_jam",
  "ondo_direct",
  "reality_via_bitget",
  "lifi",
  "relay",
]);

const ExecutionCallbackEventTypeSchema = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .regex(
    /^[A-Za-z0-9_-]+$/u,
    "callback event type may contain only letters, digits, hyphens, and underscores",
  );

const LOOPBACK_CALLBACK_HOSTNAMES = new Set([
  "localhost",
  "127.0.0.1",
  "[::1]",
  "::1",
]);

const HttpsUrlSchema = z
  .string()
  .url()
  .refine((value) => {
    try {
      const url = new URL(value);
      if (url.protocol === "https:") return true;
      return (
        url.protocol === "http:" &&
        LOOPBACK_CALLBACK_HOSTNAMES.has(url.hostname)
      );
    } catch {
      return false;
    }
  }, "callback url must use https (http is allowed only for loopback hosts)");

export const ExecutionCallbackSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("http"),
    url: HttpsUrlSchema,
    eventType: ExecutionCallbackEventTypeSchema,
  }),
  z.object({
    kind: z.literal("automation_run"),
    runId: z.string().trim().min(1),
    eventType: ExecutionCallbackEventTypeSchema,
  }),
]);

export const ExecutionCompletionTargetSchema = z.enum([
  "submitted",
  "confirmed",
  "settled",
]);

export const ExecutionSvmCommitmentSchema = z.enum([
  "processed",
  "confirmed",
  "finalized",
]);

export const ExecutionFinalityPolicySchema = z
  .object({
    evmConfirmations: z.number().int().positive().optional(),
    svmCommitment: ExecutionSvmCommitmentSchema.optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.evmConfirmations !== undefined || value.svmCommitment !== undefined,
    {
      message: "finality policy must include at least one chain finality field",
    },
  );

export const ExecutionReorgPolicySchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("fail") }).strict(),
  z.object({ type: z.literal("notify") }).strict(),
  z
    .object({
      type: z.literal("retry_execution"),
      maxAttempts: z.number().int().positive(),
    })
    .strict(),
  z
    .object({
      type: z.literal("call_task"),
      taskName: z.string().trim().min(1),
    })
    .strict(),
]);

export const TransferDisplaySchema = z
  .object({
    amount: z.string().trim().min(1).max(64).optional(),
    assetSymbol: z.string().trim().min(1).max(32).optional(),
    fromPortfolioName: z.string().trim().min(1).max(120).optional(),
    toPortfolioName: z.string().trim().min(1).max(120).optional(),
  })
  .strict();

export const WithdrawDisplaySchema = z
  .object({
    amount: z.string().trim().min(1).max(64).optional(),
    assetSymbol: z.string().trim().min(1).max(32).optional(),
    sourcePortfolioName: z.string().trim().min(1).max(120).optional(),
    recipientLabel: z.string().trim().min(1).max(120).optional(),
  })
  .strict();

export const DepositTransferReferenceSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("evm_tx_hash"),
      value: EvmTxHashSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal("svm_signature"),
      value: z.string().trim().min(1).max(128),
    })
    .strict(),
]);

const specDrivenBuyRequestShape = {
  requestKey: ExecutionRequestKeySchema,
  correlationId: ExecutionCorrelationIdSchema.optional(),
  portfolioId: ExecutionPortfolioIdSchema,
  buyAssetId: EvmErc20AssetIdSchema,
  spendRaw: PositiveIntegerAmountSchema,
  destinationAccountRef: EvmAccountIdSchema.optional(),
} satisfies z.ZodRawShape;

const refineSpecDrivenBuyDestinationChain = (
  input: z.infer<z.ZodObject<typeof specDrivenBuyRequestShape>>,
  ctx: z.RefinementCtx,
): void => {
  if (input.destinationAccountRef === undefined) return;
  const destination = Parser.parseAccountId(input.destinationAccountRef);
  const buy = Parser.parseAssetId(input.buyAssetId as AssetId);
  if (destination.chain.reference !== buy.chain.reference) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "destination account must be on the buy asset chain",
      path: ["destinationAccountRef"],
    });
  }
};

export const specDrivenBuyPreviewSchema = z
  .object({
    ...specDrivenBuyRequestShape,
    reviewedGrossCapRaw: PositiveIntegerAmountSchema,
  })
  .strict()
  .superRefine((input, ctx) => {
    refineSpecDrivenBuyDestinationChain(input, ctx);
    if (BigInt(input.reviewedGrossCapRaw) < BigInt(input.spendRaw)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "reviewed gross cap cannot be lower than preview spend",
        path: ["reviewedGrossCapRaw"],
      });
    }
  });

export const SpecDrivenBuyFundLegWireSchema = z
  .object({
    sourceChainRef: ChainIdSchema,
    sourceAccountRef: AccountIdSchema,
    sourceAssetId: AssetIdSchema,
    sourceAmountRaw: PositiveIntegerAmountSchema,
    minDestinationAmountRaw: PositiveIntegerAmountSchema,
    slippageBps: z.number().int().min(0).max(10_000).optional(),
    rail: z.enum([
      "zerodev_routing_address",
      "lifi_bridge",
      "cross_chain_swap",
      "same_chain_swap",
      "float_transfer",
    ]),
    settlement: z.enum(["separate_action", "settles_with_trade"]).optional(),
    floatAdvance: z
      .object({
        recoveryChainRef: ChainIdSchema,
        recoveryAccountRef: AccountIdSchema,
        recoveryAssetId: AssetIdSchema,
        recoveryAmountRaw: PositiveIntegerAmountSchema,
        recoveryAuthority: z
          .object({
            kind: z.literal("delegated_session"),
            sessionKeyId: z.number().int().positive().safe(),
            agentId: z.number().int().positive().safe(),
          })
          .strict()
          .optional(),
      })
      .strict()
      .optional(),
    aggregatorProvider: z.enum(["lifi", "relay"]).optional(),
    railQuoteRef: z.string().min(1).optional(),
  })
  .strict()
  .superRefine((leg, ctx) => {
    // Parent refinements also run when a child refinement has added issues.
    // Keep malformed CAIP inputs inside safeParse's ordinary failure result.
    if (
      !Validator.isValidAssetId(leg.sourceAssetId) ||
      !Validator.isValidAccountId(leg.sourceAccountRef)
    )
      return;
    const sourceAsset = Parser.parseAssetId(leg.sourceAssetId);
    const sourceAccount = Parser.parseAccountId(leg.sourceAccountRef);
    const sourceAssetChainRef = `${sourceAsset.chain.namespace}:${sourceAsset.chain.reference}`;
    const sourceAccountChainRef = `${sourceAccount.chain.namespace}:${sourceAccount.chain.reference}`;
    if (
      sourceAssetChainRef !== leg.sourceChainRef ||
      sourceAccountChainRef !== leg.sourceChainRef
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "fund leg source asset and account must be on the source chain",
        path: ["sourceChainRef"],
      });
    }
    if (
      (leg.rail === "cross_chain_swap" &&
        leg.aggregatorProvider === undefined) ||
      (leg.rail === "lifi_bridge" && leg.aggregatorProvider === "relay") ||
      (leg.rail === "float_transfer" && leg.aggregatorProvider === "lifi")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "fund leg provider must match the selected rail",
        path: ["aggregatorProvider"],
      });
    }
    if ((leg.rail === "float_transfer") !== (leg.floatAdvance !== undefined)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "floatAdvance must be present if and only if rail is float_transfer",
        path: ["floatAdvance"],
      });
    }
    if (leg.settlement !== undefined && leg.rail !== "float_transfer") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "fund leg settlement mode is only legal for float_transfer",
        path: ["settlement"],
      });
    }
  });

export const SpecDrivenBuyFundPlanWireSchema = z
  .object({
    kind: z.literal("fund"),
    requiredAssetId: AssetIdSchema,
    requiredAmountRaw: PositiveIntegerAmountSchema,
    legs: z.array(SpecDrivenBuyFundLegWireSchema).min(1).max(8),
    constraints: z
      .object({
        maxFundingCostBps: z.number().int().min(0).max(1_000),
        fundingTimeoutAt: z.string().datetime({ offset: true }),
        topUp: z
          .object({ maxAttempts: z.number().int().min(0).max(3) })
          .strict(),
      })
      .strict(),
  })
  .strict()
  .superRefine((plan, ctx) => {
    if (!Validator.isValidAssetId(plan.requiredAssetId)) return;
    const destinationAsset = Parser.parseAssetId(plan.requiredAssetId);
    const destinationChainRef = `${destinationAsset.chain.namespace}:${destinationAsset.chain.reference}`;
    for (const [index, leg] of plan.legs.entries()) {
      if (
        (leg.rail === "lifi_bridge" || leg.rail === "cross_chain_swap") &&
        leg.sourceChainRef === destinationChainRef
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "cross-chain fund leg must differ from the destination chain",
          path: ["legs", index, "sourceChainRef"],
        });
      }
      if (
        leg.rail === "same_chain_swap" &&
        leg.sourceChainRef !== destinationChainRef
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "same-chain fund leg must use the destination chain",
          path: ["legs", index, "sourceChainRef"],
        });
      }
    }
  });

export const SpecDrivenBuyFundingExplanationWireSchema = z
  .object({
    requiredAssetId: AssetIdSchema,
    requiredAssetDecimals: z.number().int().nonnegative(),
    destinationSpendableRaw: NonNegativeIntegerAmountSchema,
    nettedRaw: NonNegativeIntegerAmountSchema,
    shortfallRaw: NonNegativeIntegerAmountSchema,
    terminalDeliverableRaw: NonNegativeIntegerAmountSchema,
    legs: z.array(
      z
        .object({
          source: z
            .object({
              chainRef: ChainIdSchema,
              accountRef: AccountIdSchema,
              assetId: AssetIdSchema,
              symbol: z.string().min(1),
              decimals: z.number().int().nonnegative(),
              amountRaw: PositiveIntegerAmountSchema,
            })
            .strict(),
          terminal: z
            .object({
              assetId: AssetIdSchema,
              decimals: z.number().int().nonnegative(),
              minAmountRaw: PositiveIntegerAmountSchema,
            })
            .strict(),
          rail: z.enum([
            "zerodev_routing_address",
            "lifi_bridge",
            "cross_chain_swap",
            "same_chain_swap",
            "float_transfer",
          ]),
          slippageBps: z.number().int().min(0).max(10_000).optional(),
          aggregatorProvider: z.enum(["lifi", "relay"]).optional(),
          railQuoteRef: z.string().min(1).optional(),
          expectedCostBps: z.number().int().nonnegative(),
          etaSeconds: z.number().int().nonnegative().optional(),
          paddingBps: z.number().int().nonnegative(),
          expectedCostScore: NonNegativeIntegerAmountSchema,
        })
        .strict(),
    ),
    candidatePlanCount: z.number().int().nonnegative(),
    searchTruncation: z
      .object({
        maxSourcesPerAssetChain: z.number().int().positive(),
        omittedSourceCount: z.number().int().nonnegative(),
        groups: z.array(
          z
            .object({
              chainRef: ChainIdSchema,
              assetId: AssetIdSchema,
              sourceCount: z.number().int().nonnegative(),
              consideredSourceCount: z.number().int().nonnegative(),
            })
            .strict(),
        ),
        quoteSearch: z
          .object({
            reason: z.literal("quote_replan_budget_exhausted"),
            maxReplanIterations: z.number().int().positive(),
            untriedCandidateCount: z.number().int().positive(),
          })
          .strict()
          .optional(),
      })
      .strict()
      .optional(),
    // Present only on an unfunded result: sources whose provider quote
    // refused to fund the request, and why.
    railRejections: z
      .array(
        z
          .object({
            rail: z.enum([
              "zerodev_routing_address",
              "lifi_bridge",
              "cross_chain_swap",
              "same_chain_swap",
              "float_transfer",
            ]),
            aggregatorProvider: z.enum(["lifi", "relay"]).optional(),
            sourceChainRef: ChainIdSchema,
            sourceAssetId: AssetIdSchema,
            // A string, not an enum: the engine may add reasons before every
            // deployed client knows them.
            reason: z.string().min(1),
          })
          .strict(),
      )
      .optional(),
    decidedBy: z.discriminatedUnion("criterion", [
      z.object({ criterion: z.literal("only_viable_plan") }).strict(),
      z.object({ criterion: z.literal("no_viable_plan") }).strict(),
      z
        .object({
          criterion: z.literal("max_terminal_deliverable"),
          winnerTerminalRaw: NonNegativeIntegerAmountSchema,
          runnerUpTerminalRaw: NonNegativeIntegerAmountSchema,
        })
        .strict(),
      z
        .object({
          criterion: z.literal("fewest_legs"),
          winnerLegCount: z.number().int().nonnegative(),
          runnerUpLegCount: z.number().int().nonnegative(),
        })
        .strict(),
      z
        .object({
          criterion: z.literal("lowest_expected_cost"),
          commonDecimals: z.number().int().nonnegative(),
          winnerExpectedCostScore: NonNegativeIntegerAmountSchema,
          runnerUpExpectedCostScore: NonNegativeIntegerAmountSchema,
        })
        .strict(),
      z
        .object({
          criterion: z.literal("priority_order"),
          winnerPriorityOrder: z.array(z.number().int().nonnegative()),
          runnerUpPriorityOrder: z.array(z.number().int().nonnegative()),
        })
        .strict(),
      z
        .object({
          criterion: z.literal("deterministic_tie_break"),
          winnerKey: z.string(),
          runnerUpKey: z.string(),
        })
        .strict(),
    ]),
    humanExplanation: z.string().min(1),
  })
  .strict();

const SpecDrivenBuyPlanBasisIntegritySchema = z
  .string()
  .regex(/^sbb1\.[a-f0-9]{64}$/u);

export const UnsignedSpecDrivenBuyPlanBasisSchema = z
  .object({
    buyingPowerRaw: NonNegativeIntegerAmountSchema,
    quotedSpendRaw: PositiveIntegerAmountSchema,
    expiresAt: z.number().int().nonnegative(),
    plan: SpecDrivenBuyFundPlanWireSchema,
    explanation: SpecDrivenBuyFundingExplanationWireSchema,
    waveCount: z.number().int().positive(),
  })
  .strict();

export const SpecDrivenBuyPlanBasisSchema =
  UnsignedSpecDrivenBuyPlanBasisSchema.extend({
    integrity: SpecDrivenBuyPlanBasisIntegritySchema,
  }).strict();

export const specDrivenBuyCreateSchema = z
  .object({
    ...specDrivenBuyRequestShape,
    expectedVenue: ExpectedMarketOrderVenueSchema.optional(),
    planBasis: SpecDrivenBuyPlanBasisSchema,
  })
  .strict()
  .superRefine(refineSpecDrivenBuyDestinationChain);

// Client-facing names retained as aliases so adopting the shared package does
// not require a public API rename; the Worker uses the shorter server names.
export const executionSpecDrivenBuyPreviewSchema = specDrivenBuyPreviewSchema;
export const executionSpecDrivenBuyCreateSchema = specDrivenBuyCreateSchema;

export const RawExecutionActionInputSchema = z.object({
  type: z.string().min(1, "action type is required"),
  scope: ExecutionActionScopeWireSchema,
  args: z.unknown(),
  failurePolicy: ExecutionActionFailurePolicySchema.optional(),
});

type MarketOrderChainIdentityInput = {
  readonly chainRef?: string;
  readonly chainId?: number;
};

type ParsedMarketOrderChainRef =
  | { readonly success: true; readonly chainId: number }
  | {
      readonly success: false;
      readonly reason: "invalid" | "invalid_eip155" | "unsupported_namespace";
    };

const parseMarketOrderChainRef = (
  chainRef: string,
): ParsedMarketOrderChainRef => {
  let parsed: ReturnType<typeof Parser.parseChainId>;
  try {
    parsed = Parser.parseChainId(chainRef);
  } catch {
    return { success: false, reason: "invalid" };
  }

  if (parsed.chain.namespace !== "eip155") {
    return { success: false, reason: "unsupported_namespace" };
  }
  if (!/^\d+$/u.test(parsed.chain.reference)) {
    return { success: false, reason: "invalid_eip155" };
  }

  const chainId = Number(parsed.chain.reference);
  if (!Number.isSafeInteger(chainId) || chainId <= 0) {
    return { success: false, reason: "invalid_eip155" };
  }

  return { success: true, chainId };
};

const addMarketOrderChainRefIssue = (
  chainRef: string,
  ctx: z.RefinementCtx,
): number | null => {
  const parsed = parseMarketOrderChainRef(chainRef);
  if (parsed.success) return parsed.chainId;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    message:
      parsed.reason === "unsupported_namespace"
        ? "market orders support eip155 chains only"
        : parsed.reason === "invalid_eip155"
          ? "eip155 chainRef must contain a safe positive decimal chain id"
          : "chainRef must be a valid CAIP-2 chain reference",
    path: ["chainRef"],
  });
  return null;
};

const refineRawMarketOrderChainIdentity = (
  input: MarketOrderChainIdentityInput,
  ctx: z.RefinementCtx,
): void => {
  const hasChainRef = input.chainRef !== undefined;
  const hasChainId = input.chainId !== undefined;
  if (hasChainRef === hasChainId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "exactly one of chainRef or chainId is required",
      path: [hasChainRef ? "chainRef" : "chainId"],
    });
    return;
  }

  if (input.chainRef !== undefined) {
    addMarketOrderChainRefIssue(input.chainRef, ctx);
  }
};

const refineNormalizedMarketOrderChainIdentity = (
  input: Required<MarketOrderChainIdentityInput>,
  ctx: z.RefinementCtx,
): void => {
  const derivedChainId = addMarketOrderChainRefIssue(input.chainRef, ctx);
  if (derivedChainId !== null && input.chainId !== derivedChainId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "chainId must match the eip155 chainRef",
      path: ["chainId"],
    });
  }
};

function normalizeMarketOrderChainIdentity<
  TInput extends MarketOrderChainIdentityInput,
>(
  input: TInput,
): TInput & { readonly chainRef: string; readonly chainId: number };
function normalizeMarketOrderChainIdentity(
  input: MarketOrderChainIdentityInput,
) {
  let chainId = input.chainId;
  if (chainId === undefined) {
    if (input.chainRef === undefined) {
      throw new Error("market-order chain identity invariant violated");
    }
    const parsed = parseMarketOrderChainRef(input.chainRef);
    if (!parsed.success) {
      throw new Error("market-order chainRef refinement invariant violated");
    }
    chainId = parsed.chainId;
  }

  return {
    ...input,
    chainRef: `eip155:${chainId}`,
    chainId,
  };
}

const readEvmTradableAsset = (
  assetId: string,
): { readonly chainId: number; readonly kind: "erc20" | "native" } | null => {
  try {
    const parsed = Parser.parseAssetId(assetId as AssetId);
    if (parsed.chain.namespace !== "eip155") return null;
    const chainId = Number.parseInt(parsed.chain.reference, 10);
    if (!Number.isSafeInteger(chainId) || chainId <= 0) return null;
    if (parsed.asset.namespace === "erc20") {
      return { chainId, kind: "erc20" };
    }
    if (
      parsed.asset.namespace === "slip44" &&
      parsed.asset.reference === "60"
    ) {
      return { chainId, kind: "native" };
    }
    return null;
  } catch {
    return null;
  }
};

const MarketOrderIntentShape = {
  requestKey: ExecutionRequestKeySchema,
  correlationId: ExecutionCorrelationIdSchema.optional(),
  portfolioId: ExecutionPortfolioIdSchema,
  sellAssetId: AssetIdSchema,
  buyAssetId: AssetIdSchema,
  sellAmount: PositiveIntegerAmountSchema,
  provider: MarketOrderRfqProviderSchema.optional(),
  to: EvmAddressSchema.optional(),
  toPortfolioId: ExecutionPortfolioIdSchema.optional(),
  expiryType: z.enum(["standard", "short"]).optional(),
  feeBps: z.number().int().min(0).max(500).optional(),
} satisfies z.ZodRawShape;

const RawMarketOrderIntentSchema = z.object({
  ...MarketOrderIntentShape,
  chainId: z.number().int().positive().optional(),
  chainRef: z.string().min(1).optional(),
});

const NormalizedMarketOrderIntentSchema = z.object({
  ...MarketOrderIntentShape,
  chainId: z.number().int().positive(),
  chainRef: z.string().min(1),
});

const MarketOrderCreateExtensionShape = {
  quoteVenueKey: LifiMarketOrderVenueKeySchema.optional(),
  expectedVenue: ExpectedMarketOrderVenueSchema.optional(),
  completionTarget: ExecutionCompletionTargetSchema.optional(),
  callback: ExecutionCallbackSchema.optional(),
} satisfies z.ZodRawShape;

export type ExecutionMarketOrderVenueRefinementInput = {
  readonly buyAssetId: string;
  readonly chainId: number;
  readonly chainRef: string;
  readonly provider?: z.infer<typeof MarketOrderRfqProviderSchema>;
  readonly sellAssetId: string;
};

/**
 * App-local policy hook for provider capabilities and chain cash registries.
 * Inputs have validated CAIP syntax and normalized chain identity; the custom
 * refiner owns EVM venue admission when supplied.
 */
export type ExecutionMarketOrderVenueRefinement = (
  input: ExecutionMarketOrderVenueRefinementInput,
  ctx: z.RefinementCtx,
) => void;

export type ExecutionMarketOrderSchemaFactoryOptions = {
  readonly refineVenueCapabilities?: ExecutionMarketOrderVenueRefinement;
};

const refinePureMarketOrderIntent = (
  input: ExecutionMarketOrderVenueRefinementInput,
  ctx: z.RefinementCtx,
): void => {
  for (const side of ["sellAssetId", "buyAssetId"] as const) {
    const asset = readEvmTradableAsset(input[side]);
    if (!asset || asset.chainId !== input.chainId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${side} must be an EVM token asset on chainId`,
        path: [side],
      });
    }
  }
};

/**
 * Builds market-order schemas with an optional app-owned provider refinement.
 * Consumers that do not own venue knowledge can use the exported default
 * schemas, which still enforce every pure wire invariant.
 */
export const createExecutionMarketOrderRequestSchemas = (
  options: ExecutionMarketOrderSchemaFactoryOptions = {},
) => {
  const refineIntent = (
    input: z.infer<typeof NormalizedMarketOrderIntentSchema>,
    ctx: z.RefinementCtx,
  ): void => {
    if (input.to && input.toPortfolioId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "only one destination may be provided",
        path: ["to"],
      });
    }
    if (options.refineVenueCapabilities) {
      options.refineVenueCapabilities(input, ctx);
    } else {
      refinePureMarketOrderIntent(input, ctx);
    }
  };

  const executionMarketOrderCreateSchema = z
    .union([
      RawMarketOrderIntentSchema.extend(MarketOrderCreateExtensionShape)
        .superRefine(refineRawMarketOrderChainIdentity)
        .transform(normalizeMarketOrderChainIdentity),
      NormalizedMarketOrderIntentSchema.extend(MarketOrderCreateExtensionShape)
        .superRefine(refineNormalizedMarketOrderChainIdentity)
        .transform(normalizeMarketOrderChainIdentity),
    ])
    .superRefine(refineIntent);

  const executionMarketOrderQuotePreviewSchema = z
    .union([
      RawMarketOrderIntentSchema.strict()
        .superRefine(refineRawMarketOrderChainIdentity)
        .transform(normalizeMarketOrderChainIdentity),
      NormalizedMarketOrderIntentSchema.strict()
        .superRefine(refineNormalizedMarketOrderChainIdentity)
        .transform(normalizeMarketOrderChainIdentity),
    ])
    .superRefine(refineIntent);

  return {
    executionMarketOrderCreateSchema,
    executionMarketOrderQuotePreviewSchema,
  };
};

const defaultMarketOrderRequestSchemas =
  createExecutionMarketOrderRequestSchemas();

/**
 * Admission-lax defaults for clients without venue policy. The Worker layers
 * refineEvmMarketOrderRoute through createExecutionMarketOrderRequestSchemas.
 */
export const executionMarketOrderCreateSchema =
  defaultMarketOrderRequestSchemas.executionMarketOrderCreateSchema;
export const executionMarketOrderQuotePreviewSchema =
  defaultMarketOrderRequestSchemas.executionMarketOrderQuotePreviewSchema;

export const MAX_EXECUTION_OPERATION_ACTIONS = 50;

export const liquidationCreateSchema = z
  .object({
    portfolioId: ExecutionPortfolioIdSchema,
    chainId: z.number().int().positive(),
    assets: z
      .array(EvmErc20AssetIdSchema)
      .min(1)
      .max(MAX_EXECUTION_OPERATION_ACTIONS),
    settlementAssetId: EvmErc20AssetIdSchema,
    slippageBps: z.number().int().min(0).max(10_000).default(100),
    marketHoursPolicy: z.enum(["hold", "reject"]).default("hold"),
    partialDelivery: z.enum(["deliver", "withhold"]).default("deliver"),
    recipient: EvmAddressSchema.optional(),
    idempotencyKey: ExecutionRequestKeySchema,
    correlationId: ExecutionCorrelationIdSchema.optional(),
  })
  .strict()
  .superRefine((input, ctx) => {
    const seenAssets = new Set<string>();
    for (const [index, assetId] of input.assets.entries()) {
      const parsed = Parser.parseAssetId(assetId as AssetId);
      if (parsed.chain.reference !== String(input.chainId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "asset chain must match chainId",
          path: ["assets", index],
        });
      }
      if (seenAssets.has(assetId)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "assets must not contain duplicates",
          path: ["assets", index],
        });
      }
      seenAssets.add(assetId);
    }

    const settlementAsset = Parser.parseAssetId(
      input.settlementAssetId as AssetId,
    );
    if (settlementAsset.chain.reference !== String(input.chainId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "settlementAssetId chain must match chainId",
        path: ["settlementAssetId"],
      });
    }
    if (seenAssets.has(input.settlementAssetId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "settlementAssetId must not appear in assets",
        path: ["settlementAssetId"],
      });
    }
  });

const MarketOrderReferenceShape = {
  flow: z.literal("market_order"),
  provider: MarketOrderExecutionProviderSchema,
  intentSide: z.enum(["buy", "sell", "swap"]),
  sellAssetId: AssetIdSchema,
  buyAssetId: AssetIdSchema,
  sellAmount: PositiveIntegerAmountSchema,
  cashAssetId: AssetIdSchema.nullable(),
  quoteAssetId: AssetIdSchema.nullable(),
} satisfies z.ZodRawShape;

const RawMarketOrderReferenceSchema = z
  .object({
    ...MarketOrderReferenceShape,
    chainId: z.number().int().positive().optional(),
    chainRef: z.string().min(1).optional(),
  })
  .strict()
  .superRefine(refineRawMarketOrderChainIdentity)
  .transform(normalizeMarketOrderChainIdentity);

const NormalizedMarketOrderReferenceSchema = z
  .object({
    ...MarketOrderReferenceShape,
    chainId: z.number().int().positive(),
    chainRef: z.string().min(1),
  })
  .strict()
  .superRefine(refineNormalizedMarketOrderChainIdentity)
  .transform(normalizeMarketOrderChainIdentity);

export const MarketOrderReferenceWireSchema = z.union([
  RawMarketOrderReferenceSchema,
  NormalizedMarketOrderReferenceSchema,
]);

export const ExecutionOperationCreateShape = {
  requestKey: ExecutionRequestKeySchema,
  correlationId: ExecutionCorrelationIdSchema.optional(),
  initiatorOwnerAddress: z.string().trim().min(1).optional(),
  actions: z
    .array(RawExecutionActionInputSchema)
    .min(1)
    .max(MAX_EXECUTION_OPERATION_ACTIONS),
  completionTarget: ExecutionCompletionTargetSchema.optional(),
  finality: ExecutionFinalityPolicySchema.optional(),
  operationKind: z
    .enum(["execution", "withdraw", "transfer", "bridge"])
    .optional(),
  onReorg: ExecutionReorgPolicySchema.optional(),
  callback: ExecutionCallbackSchema.optional(),
  requiresInternalExecution: z.boolean().optional(),
  transferDisplay: TransferDisplaySchema.optional(),
  withdrawDisplay: WithdrawDisplaySchema.optional(),
  marketOrder: MarketOrderReferenceWireSchema.optional(),
} satisfies z.ZodRawShape;

export const executionOperationCreateSchema = z
  .object(ExecutionOperationCreateShape)
  .strict()
  .superRefine((input, ctx) => {
    for (const [actionIndex, action] of input.actions.entries()) {
      if (
        action.type === "owner_withdrawal_batch" ||
        action.type === "external_withdrawal_batch"
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Withdrawal batches require their dedicated withdrawal route",
          path: ["actions", actionIndex, "type"],
        });
      }
      if (
        typeof action.args !== "object" ||
        action.args === null ||
        Array.isArray(action.args)
      ) {
        continue;
      }
      for (const key of Object.keys(action.args)) {
        if (
          key !== "bridgeIntentPurpose" &&
          key !== "executionAccountRef" &&
          !/^funding.*AccountRef$/u.test(key)
        ) {
          continue;
        }
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${key} is reserved for internal compiler payloads`,
          path: ["actions", actionIndex, "args", key],
        });
      }
    }
  });

const readEvmAssetChainId = (assetId: string): number | null => {
  try {
    const parsed = Parser.parseAssetId(assetId as AssetId);
    if (parsed.chain.namespace !== "eip155") return null;
    const chainId = Number.parseInt(parsed.chain.reference, 10);
    return Number.isSafeInteger(chainId) && chainId > 0 ? chainId : null;
  } catch {
    return null;
  }
};

const readEvmErc20Asset = (
  assetId: string,
): { readonly address: string; readonly chainId: number } | null => {
  try {
    const parsed = Parser.parseAssetId(assetId as AssetId);
    if (
      parsed.chain.namespace !== "eip155" ||
      parsed.asset.namespace !== "erc20" ||
      !/^0x[a-fA-F0-9]{40}$/u.test(parsed.asset.reference)
    ) {
      return null;
    }
    const chainId = Number.parseInt(parsed.chain.reference, 10);
    if (!Number.isSafeInteger(chainId) || chainId <= 0) return null;
    return { address: parsed.asset.reference.toLowerCase(), chainId };
  } catch {
    return null;
  }
};

const OndoDirectBatchSellLegSchema = z
  .object({
    assetId: AssetIdSchema,
    assetTokenAddress: EvmAddressSchema.optional(),
    amount: PositiveIntegerAmountSchema,
    minCashOutRaw: PositiveIntegerAmountSchema.optional(),
  })
  .strict();

const OndoDirectBatchBuyLegSchema = z
  .object({
    assetId: AssetIdSchema,
    assetTokenAddress: EvmAddressSchema.optional(),
    cashAmount: PositiveIntegerAmountSchema,
    minTokenOutRaw: PositiveIntegerAmountSchema.optional(),
  })
  .strict();

const validateOndoDirectBatchLegAsset = (params: {
  readonly assetId: string;
  readonly assetTokenAddress?: string;
  readonly cashAssetId: string;
  readonly chainId: number;
  readonly ctx: z.RefinementCtx;
  readonly path: readonly (string | number)[];
}): void => {
  const asset = readEvmErc20Asset(params.assetId);
  if (!asset || asset.chainId !== params.chainId) {
    params.ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "assetId must be an ERC-20 asset on chainId",
      path: [...params.path, "assetId"],
    });
    return;
  }

  if (params.assetId.toLowerCase() === params.cashAssetId.toLowerCase()) {
    params.ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "leg assetId must be different from cashAssetId",
      path: [...params.path, "assetId"],
    });
  }

  if (params.assetTokenAddress && params.assetTokenAddress !== asset.address) {
    params.ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "assetTokenAddress must match assetId",
      path: [...params.path, "assetTokenAddress"],
    });
  }
};

export const executionOndoDirectBatchRebalanceCreateSchema = z
  .object({
    requestKey: ExecutionRequestKeySchema,
    correlationId: ExecutionCorrelationIdSchema.optional(),
    portfolioId: ExecutionPortfolioIdSchema,
    chainId: z.number().int().positive(),
    cashAssetId: AssetIdSchema,
    cashTokenAddress: EvmAddressSchema,
    cashInputAmount: PositiveIntegerAmountSchema.optional(),
    sells: z.array(OndoDirectBatchSellLegSchema).max(50).optional(),
    buys: z.array(OndoDirectBatchBuyLegSchema).max(50).optional(),
    completionTarget: ExecutionCompletionTargetSchema.optional(),
    finality: ExecutionFinalityPolicySchema.optional(),
    onReorg: ExecutionReorgPolicySchema.optional(),
    callback: ExecutionCallbackSchema.optional(),
    residualCashPolicy: z.literal("sweep_to_user").optional(),
    approvalMode: z
      .enum(["require_existing", "submit_missing_approvals"])
      .optional(),
  })
  .strict()
  .superRefine((input, ctx) => {
    if ((input.sells?.length ?? 0) + (input.buys?.length ?? 0) === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "at least one Ondo Direct batch leg is required",
        path: ["buys"],
      });
    }

    const cashAsset = readEvmErc20Asset(input.cashAssetId);
    if (!cashAsset || cashAsset.chainId !== input.chainId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "cashAssetId must be an ERC-20 asset on chainId",
        path: ["cashAssetId"],
      });
    } else if (input.cashTokenAddress !== cashAsset.address) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "cashTokenAddress must match cashAssetId",
        path: ["cashTokenAddress"],
      });
    }

    const legs = [
      ...(input.sells ?? []).map((leg, index) => ({
        leg,
        side: "sells" as const,
        index,
      })),
      ...(input.buys ?? []).map((leg, index) => ({
        leg,
        side: "buys" as const,
        index,
      })),
    ];

    for (const { leg, side, index } of legs) {
      const path = [side, index] as const;
      validateOndoDirectBatchLegAsset({
        assetId: leg.assetId,
        assetTokenAddress: leg.assetTokenAddress,
        cashAssetId: input.cashAssetId,
        chainId: input.chainId,
        ctx,
        path,
      });
    }
  });

export const OndoDirectRebalanceTargetSchema = z
  .object({
    assetId: AssetIdSchema,
    weightBps: z.number().int().min(0).max(10_000),
  })
  .strict();

const refineOndoDirectRebalancePlanInput = (
  input: {
    readonly chainId: number;
    readonly cashAssetId: string;
    readonly cashTokenAddress: string;
    readonly targets: readonly { assetId: string; weightBps: number }[];
    readonly positions?: readonly string[];
  },
  ctx: z.RefinementCtx,
): void => {
  const cashAsset = readEvmErc20Asset(input.cashAssetId);
  if (!cashAsset || cashAsset.chainId !== input.chainId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "cashAssetId must be an ERC-20 asset on chainId",
      path: ["cashAssetId"],
    });
  } else if (input.cashTokenAddress !== cashAsset.address) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "cashTokenAddress must match cashAssetId",
      path: ["cashTokenAddress"],
    });
  }

  const seenTargets = new Set<string>();
  let weightSum = 0;
  for (const [index, target] of input.targets.entries()) {
    weightSum += target.weightBps;
    const targetKey = target.assetId.toLowerCase();
    if (seenTargets.has(targetKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "duplicate target assetId",
        path: ["targets", index, "assetId"],
      });
    }
    seenTargets.add(targetKey);

    if (targetKey === input.cashAssetId.toLowerCase()) continue;
    const asset = readEvmErc20Asset(target.assetId);
    if (!asset || asset.chainId !== input.chainId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "target assetId must be an ERC-20 asset on chainId",
        path: ["targets", index, "assetId"],
      });
    }
  }
  if (weightSum !== 10_000) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "target weights must sum to exactly 10000 basis points",
      path: ["targets"],
    });
  }

  for (const [index, assetId] of (input.positions ?? []).entries()) {
    if (assetId.toLowerCase() === input.cashAssetId.toLowerCase()) continue;
    const asset = readEvmErc20Asset(assetId);
    if (!asset || asset.chainId !== input.chainId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "position assetId must be an ERC-20 asset on chainId",
        path: ["positions", index],
      });
    }
  }
};

export const executionOndoDirectRebalancePlanPreviewSchema = z
  .object({
    portfolioId: ExecutionPortfolioIdSchema,
    chainId: z.number().int().positive(),
    cashAssetId: AssetIdSchema,
    cashTokenAddress: EvmAddressSchema,
    targets: z.array(OndoDirectRebalanceTargetSchema).min(1).max(50),
    positions: z.array(AssetIdSchema).max(100).optional(),
  })
  .strict()
  .superRefine(refineOndoDirectRebalancePlanInput);

export const executionOndoDirectRebalancePlanCreateSchema = z
  .object({
    requestKey: ExecutionRequestKeySchema,
    correlationId: ExecutionCorrelationIdSchema.optional(),
    portfolioId: ExecutionPortfolioIdSchema,
    chainId: z.number().int().positive(),
    cashAssetId: AssetIdSchema,
    cashTokenAddress: EvmAddressSchema,
    targets: z.array(OndoDirectRebalanceTargetSchema).min(1).max(50),
    positions: z.array(AssetIdSchema).max(100).optional(),
    completionTarget: ExecutionCompletionTargetSchema.optional(),
    finality: ExecutionFinalityPolicySchema.optional(),
    onReorg: ExecutionReorgPolicySchema.optional(),
    callback: ExecutionCallbackSchema.optional(),
    residualCashPolicy: z.literal("sweep_to_user").optional(),
    approvalMode: z
      .enum(["require_existing", "submit_missing_approvals"])
      .optional(),
  })
  .strict()
  .superRefine(refineOndoDirectRebalancePlanInput);

/** Owner-only in-kind withdrawal. Financial facts and the recipient are server-owned. */
export const executionOwnerWithdrawalCreateSchema = z
  .object({
    requestKey: ExecutionRequestKeySchema,
    correlationId: ExecutionCorrelationIdSchema.optional(),
    portfolioId: ExecutionPortfolioIdSchema,
    expectedOwnerAddress: EvmAddressSchema,
    assets: z
      .array(
        z
          .object({
            accountRef: z.string(),
            assetId: z.string(),
            amountRaw: PositiveIntegerAmountSchema.refine(
              (value) =>
                /^[1-9]\d*$/u.test(value) && BigInt(value) < 1n << 256n,
              { message: "Amount must fit an EVM uint256" },
            ),
          })
          .strict(),
      )
      .min(1)
      .max(MAX_EXECUTION_OPERATION_ACTIONS),
  })
  .strict()
  .superRefine((input, ctx) => {
    let previous: string | undefined;
    input.assets.forEach((asset, index) => {
      if (
        !SellBalanceInputSchema.safeParse({
          portfolioId: input.portfolioId,
          accountRef: asset.accountRef,
          assetId: asset.assetId,
        }).success
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["assets", index],
          message:
            "Account and asset must identify the same canonical EVM chain",
        });
      }
      const key = `${asset.accountRef}/${asset.assetId}`;
      if (previous !== undefined && previous >= key) {
        ctx.addIssue({
          code: "custom",
          path: ["assets", index],
          message:
            "Assets must be sorted by accountRef and assetId, without duplicates",
        });
      }
      previous = key;
    });
  });

export const executionWithdrawalCreateSchema = z
  .object({
    requestKey: z.string().trim().min(1),
    correlationId: z.string().trim().min(1).optional(),
    portfolioId: z.string().trim().min(1),
    chainId: z.number().int().positive(),
    assetId: AssetIdSchema,
    amount: PositiveIntegerAmountSchema,
    recipient: EvmAddressSchema,
    completionTarget: ExecutionCompletionTargetSchema.default("settled"),
    display: WithdrawDisplaySchema.optional(),
    callback: ExecutionCallbackSchema.optional(),
  })
  .superRefine((input, ctx) => {
    if (readEvmAssetChainId(input.assetId) !== input.chainId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "assetId must be an EVM asset on chainId",
        path: ["assetId"],
      });
    }
  });

const TransferAssetSchema = z.object({
  assetId: AssetIdSchema,
  amount: PositiveIntegerAmountSchema,
});

export const executionTransferCreateSchema = z
  .object({
    requestKey: z.string().trim().min(1),
    correlationId: z.string().trim().min(1).optional(),
    fromPortfolioId: z.string().trim().min(1),
    toPortfolioId: z.string().trim().min(1),
    chainId: z.number().int().positive(),
    assetId: AssetIdSchema.optional(),
    amount: PositiveIntegerAmountSchema.optional(),
    assets: z.array(TransferAssetSchema).min(1).max(20).optional(),
    completionTarget: ExecutionCompletionTargetSchema.default("settled"),
    display: TransferDisplaySchema.optional(),
    callback: ExecutionCallbackSchema.optional(),
  })
  .superRefine((input, ctx) => {
    if (input.fromPortfolioId === input.toPortfolioId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "transfer destination must be a different portfolio",
        path: ["toPortfolioId"],
      });
    }

    if (input.assets && (input.assetId || input.amount)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "provide either assets or assetId/amount",
        path: ["assets"],
      });
    }

    if (!input.assets && (!input.assetId || !input.amount)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "transfer asset and amount are required",
        path: ["assetId"],
      });
      return;
    }

    const transferAssets = input.assets ?? [
      { assetId: input.assetId as AssetId, amount: input.amount as string },
    ];
    for (const [index, asset] of transferAssets.entries()) {
      if (readEvmAssetChainId(asset.assetId) === input.chainId) continue;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "assetId must be an EVM asset on chainId",
        path: input.assets ? ["assets", index, "assetId"] : ["assetId"],
      });
    }
  });

export const executionBridgeCreateSchema = z
  .object({
    requestKey: z.string().trim().min(1),
    correlationId: z.string().trim().min(1).optional(),
    portfolioId: z.string().trim().min(1),
    provider: z.enum(["lifi", "relay"]).optional(),
    sourceChainId: z.number().int().positive(),
    destinationChainId: z.number().int().positive(),
    assetId: AssetIdSchema,
    amount: PositiveIntegerAmountSchema,
    destinationAssetId: AssetIdSchema,
    slippageBps: z.number().int().min(0).max(10_000).optional(),
    completionTarget: ExecutionCompletionTargetSchema.default("settled"),
    display: TransferDisplaySchema.optional(),
    callback: ExecutionCallbackSchema.optional(),
  })
  .superRefine((input, ctx) => {
    if (input.sourceChainId === input.destinationChainId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "sourceChainId and destinationChainId must differ",
        path: ["destinationChainId"],
      });
    }

    if (readEvmAssetChainId(input.assetId) !== input.sourceChainId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "assetId must be an EVM asset on sourceChainId",
        path: ["assetId"],
      });
    }

    if (
      readEvmAssetChainId(input.destinationAssetId) !== input.destinationChainId
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "destinationAssetId must be an EVM asset on destinationChainId",
        path: ["destinationAssetId"],
      });
    }
  });

export const executionDepositIntentCreateSchema = z
  .object({
    requestKey: z.string().trim().min(1),
    correlationId: z.string().trim().min(1).optional(),
    portfolioId: z.string().trim().min(1),
    chainId: z.number().int().positive(),
    assetId: AssetIdSchema.optional(),
    amount: PositiveIntegerAmountSchema.optional(),
    vaultAddress: EvmAddressSchema.optional(),
  })
  .superRefine((input, ctx) => {
    if (input.assetId && readEvmAssetChainId(input.assetId) !== input.chainId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "assetId must be an EVM asset on chainId",
        path: ["assetId"],
      });
    }
  });

export const executionDepositTransactionAttachSchema = z
  .object({
    txHash: EvmTxHashSchema.optional(),
    reference: DepositTransferReferenceSchema.optional(),
  })
  .superRefine((input, ctx) => {
    if (!input.txHash && !input.reference) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "deposit transfer reference is required",
        path: ["reference"],
      });
      return;
    }

    if (
      input.txHash &&
      input.reference &&
      input.reference.kind !== "evm_tx_hash"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "txHash can only be paired with an evm transfer reference",
        path: ["reference"],
      });
    }

    if (
      input.txHash &&
      input.reference?.kind === "evm_tx_hash" &&
      input.reference.value.toLowerCase() !== input.txHash.toLowerCase()
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "txHash and evm transfer reference must match",
        path: ["reference"],
      });
    }
  });

const ExternalEventBaseShape = {
  signalId: ExecutionSignalIdSchema,
  actionIndex: z.number().int().nonnegative(),
  detail: z.string().trim().min(1).optional(),
  data: ExecutionSignalEvidenceSchema.optional(),
} as const;

const executionOperationEventWireSchema = z.discriminatedUnion("kind", [
  z
    .object({
      ...ExternalEventBaseShape,
      kind: z.literal("quote_ready"),
      quote: ExecutionSignalEvidenceSchema.optional(),
    })
    .strict(),
  z
    .object({
      ...ExternalEventBaseShape,
      kind: z.literal("action_progress"),
      phase: z.string().trim().min(1).max(100),
    })
    .strict(),
  z
    .object({
      ...ExternalEventBaseShape,
      kind: z.literal("action_submitted"),
      submission: ExecutionSignalEvidenceSchema.optional(),
    })
    .strict(),
  z
    .object({
      ...ExternalEventBaseShape,
      kind: z.literal("action_confirmed"),
      receipt: ExecutionSignalEvidenceSchema.optional(),
    })
    .strict(),
  z
    .object({
      ...ExternalEventBaseShape,
      kind: z.literal("action_settled"),
      settlement: ExecutionSignalEvidenceSchema.optional(),
    })
    .strict(),
  z
    .object({
      ...ExternalEventBaseShape,
      kind: z.literal("action_succeeded"),
      receipt: ExecutionSignalEvidenceSchema.optional(),
    })
    .strict(),
  z
    .object({
      ...ExternalEventBaseShape,
      kind: z.literal("action_failed"),
      error: z.string().trim().min(1).optional(),
    })
    .strict(),
]);

export const executionOperationEventSchema =
  executionOperationEventWireSchema.superRefine((input, ctx) => {
    addSerializedSizeIssue(input, ctx);
  });

const executionOperationSettlementAckWireSchema = z.discriminatedUnion("kind", [
  z
    .object({
      signalId: ExecutionSignalIdSchema,
      kind: z.literal("indexing"),
      actionIndex: z.number().int().nonnegative().optional(),
      detail: z.string().trim().min(1).optional(),
      data: ExecutionSignalEvidenceSchema.optional(),
    })
    .strict(),
  z
    .object({
      signalId: ExecutionSignalIdSchema,
      kind: z.literal("settled"),
      actionIndex: z.number().int().nonnegative().optional(),
      detail: z.string().trim().min(1).optional(),
      settlement: ExecutionSignalEvidenceSchema.optional(),
    })
    .strict(),
]);

export const executionOperationSettlementAckSchema =
  executionOperationSettlementAckWireSchema.superRefine((input, ctx) => {
    addSerializedSizeIssue(input, ctx);
  });

export const MAX_REBALANCE_HOLDINGS = 256;

export const RebalanceTargetEntrySchema = z
  .object({
    baseAssetId: AssetIdSchema,
    totalUsdAllocation: z.number().nonnegative(),
    totalUsdTargetedForYield: z.number().nonnegative().default(0),
    desiredYieldPositions: z
      .array(
        z
          .object({
            yieldSourceId: z.string().trim().min(1),
            usdValueTargeted: z.number().nonnegative(),
            vaultAddress: EvmAddressSchema.optional(),
          })
          .strict(),
      )
      .default([]),
  })
  .strict();

export const RebalanceTargetAllocationSchema = z
  .record(z.string(), RebalanceTargetEntrySchema)
  .superRefine((allocation, ctx) => {
    if (Object.keys(allocation).length > MAX_REBALANCE_HOLDINGS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Target allocation must contain at most ${MAX_REBALANCE_HOLDINGS} assets`,
      });
    }
  });

export const RebalanceHoldingSchema = z
  .object({
    assetId: AssetIdSchema,
    chainId: z.number().int().positive(),
    amountRaw: NonNegativeIntegerAmountSchema,
    decimals: z.number().int().min(0).max(36),
    priceUsd: z.string().trim().min(1),
    unit: z.literal("per_token"),
    asOf: z.string().datetime({ offset: true }).optional(),
    sourceRef: z.string().optional(),
  })
  .strict();

export const RebalanceIssuerMetadataSchema = z
  .record(
    z.string(),
    z.object({
      issuerId: z.string().trim().min(1),
      batchSpender: EvmAddressSchema.optional(),
    }),
  )
  .optional();

export const rebalanceCreateSchema = z
  .object({
    requestKey: ExecutionRequestKeySchema,
    correlationId: ExecutionCorrelationIdSchema.optional(),
    initiatorOwnerAddress: z.string().trim().min(1).optional(),
    portfolioId: ExecutionPortfolioIdSchema,
    chainId: z.number().int().positive().safe().optional(),
    completionTarget: ExecutionCompletionTargetSchema.optional(),
    to: RebalanceTargetAllocationSchema.optional(),
    strategy: z.record(z.string(), z.unknown()).optional(),
    executionScope: RestrictedRebalanceScopeSchema.optional(),
    currentHoldings: z
      .array(RebalanceHoldingSchema)
      .max(MAX_REBALANCE_HOLDINGS)
      .describe(
        "Deprecated for create: amounts, decimals, and prices are ignored; preview may use asset ids as a selection hint",
      ),
    vaultAddresses: z
      .record(z.string(), EvmAddressSchema)
      .describe("Accepted for compatibility and fully ignored"),
    issuerMetadata: RebalanceIssuerMetadataSchema.describe(
      "Accepted for compatibility and fully ignored",
    ),
    adapterPayload: z
      .record(z.string(), z.record(z.string(), z.unknown()))
      .describe("Accepted for compatibility and fully ignored")
      .optional(),
    inputFingerprint: z
      .string()
      .trim()
      .min(1)
      .max(512)
      .describe("Accepted for compatibility and fully ignored")
      .optional(),
    callback: ExecutionCallbackSchema.optional(),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (data.executionScope) {
      const allowed: readonly string[] = BASE_WETH_USDC_ASSET_IDS;
      if (
        data.chainId !== 8453 ||
        data.to === undefined ||
        data.strategy !== undefined ||
        Object.keys(data.to).length === 0 ||
        Object.entries(data.to).some(
          ([assetId, target]) =>
            !allowed.includes(assetId) ||
            target.baseAssetId !== assetId ||
            target.desiredYieldPositions.length !== 0 ||
            target.totalUsdTargetedForYield !== 0,
        )
      ) {
        ctx.addIssue({
          code: "custom",
          message:
            "Restricted scope requires Base WETH/USDC token targets only",
          path: ["executionScope"],
        });
      }
    }
    const selectedAssetIds = new Set(
      [
        ...Object.keys(data.to ?? {}),
        ...data.currentHoldings.map((holding) => holding.assetId),
      ].map((assetId) => canonicalizeAssetIdLike(assetId)),
    );
    if (selectedAssetIds.size > MAX_REBALANCE_HOLDINGS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Rebalance selection must contain at most ${MAX_REBALANCE_HOLDINGS} unique assets`,
        path: ["currentHoldings"],
      });
    }
    const hasTo = data.to !== undefined;
    const hasStrategy = data.strategy !== undefined;
    if (!hasTo && !hasStrategy) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Exactly one of `to` or `strategy` must be supplied",
        path: ["to"],
      });
    }
    if (hasTo && hasStrategy) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "`to` and `strategy` are mutually exclusive",
        path: ["strategy"],
      });
    }
    if (data.currentHoldings.length === 0 && data.chainId === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Empty compatibility holdings require an explicit `chainId`",
        path: ["currentHoldings"],
      });
    }
  });

export type ExecutionCallback = z.infer<typeof ExecutionCallbackSchema>;
export type ExecutionCompletionTarget = z.infer<
  typeof ExecutionCompletionTargetSchema
>;
export type ExecutionSvmCommitment = z.infer<
  typeof ExecutionSvmCommitmentSchema
>;
export type ExecutionFinalityPolicy = z.infer<
  typeof ExecutionFinalityPolicySchema
>;
export type ExecutionReorgPolicy = z.infer<typeof ExecutionReorgPolicySchema>;
export type TransferDisplay = z.infer<typeof TransferDisplaySchema>;
export type WithdrawDisplay = z.infer<typeof WithdrawDisplaySchema>;
export type RawExecutionActionInput = z.input<
  typeof RawExecutionActionInputSchema
>;
export type ValidatedExecutionActionInput = z.infer<
  typeof ExecuteActionInputSchema
>;
export type SpecDrivenBuyPreviewInput = z.infer<
  typeof specDrivenBuyPreviewSchema
>;
export type UnsignedSpecDrivenBuyPlanBasis = z.infer<
  typeof UnsignedSpecDrivenBuyPlanBasisSchema
>;
export type SpecDrivenBuyPlanBasis = z.infer<
  typeof SpecDrivenBuyPlanBasisSchema
>;
export type SpecDrivenBuyCreateInput = z.infer<
  typeof specDrivenBuyCreateSchema
>;
export type LiquidationCreateRequest = z.input<typeof liquidationCreateSchema>;
export type LiquidationCreateInput = z.output<typeof liquidationCreateSchema>;
export type ExecutionOperationCreateInput = z.input<
  typeof executionOperationCreateSchema
>;
export type ExecutionMarketOrderCreateInput = z.input<
  typeof executionMarketOrderCreateSchema
>;
export type NormalizedExecutionMarketOrderCreateInput = z.output<
  typeof executionMarketOrderCreateSchema
>;
export type ExecutionMarketOrderQuotePreviewInput = z.input<
  typeof executionMarketOrderQuotePreviewSchema
>;
export type NormalizedExecutionMarketOrderQuotePreviewInput = z.output<
  typeof executionMarketOrderQuotePreviewSchema
>;
export type ExecutionOndoDirectBatchRebalanceCreateInput = z.infer<
  typeof executionOndoDirectBatchRebalanceCreateSchema
>;
export type ExecutionOndoDirectRebalancePlanPreviewInput = z.infer<
  typeof executionOndoDirectRebalancePlanPreviewSchema
>;
export type ExecutionOndoDirectRebalancePlanCreateInput = z.infer<
  typeof executionOndoDirectRebalancePlanCreateSchema
>;
export type ExecutionOwnerWithdrawalCreateInput = z.infer<
  typeof executionOwnerWithdrawalCreateSchema
>;
export type ExecutionWithdrawalCreateInput = z.infer<
  typeof executionWithdrawalCreateSchema
>;
export type ExecutionTransferCreateInput = z.infer<
  typeof executionTransferCreateSchema
>;
export type ExecutionBridgeCreateInput = z.infer<
  typeof executionBridgeCreateSchema
>;
export type ExecutionDepositIntentCreateInput = z.infer<
  typeof executionDepositIntentCreateSchema
>;
export type ExecutionDepositTransactionAttachInput = z.infer<
  typeof executionDepositTransactionAttachSchema
>;
export type DepositTransferReference = z.infer<
  typeof DepositTransferReferenceSchema
>;
export type ExecutionOperationEventInput = z.infer<
  typeof executionOperationEventSchema
>;
export type ExecutionOperationSettlementAckInput = z.infer<
  typeof executionOperationSettlementAckSchema
>;
export type ExecutionOperationEventKind = ExecutionOperationEventInput["kind"];
export type RebalanceCreateInput = z.infer<typeof rebalanceCreateSchema>;
